<template lang="pug">
div(
    :id="id"
    :class="{ dense: dense }"
    class="tl-input-text"
    data-form-validator
)
    div(
        :class="{ 'animation-shake': error }"
        class="input-wrapper"
    )
        div(
            class="label-input-wrapper"
            :class="{ plain: plain, error: error, password: typePassword, icon: hasIcon, focus: isFocused, smallLabel: (isFocused || `${modelState}`.length) && label, disabled: disabled, opaq: opaq }"
            @click="focusInput"
        )
            div
                tl-position(
                    class="label fill-height"
                    y="center"
                )
                    div {{ label }}
                input(
                    ref="input"
                    class="input-el"
                    :disabled="disabled"
                    :maxlength="maxLength"
                    :type="type"
                    :step="step"
                    tabindex="0"
                    v-bind="inputAttrs"
                    :value="modelState"
                    @input="updateModelState"
                    @blur="isFocused = false"
                    @focus="isFocused = true"
                    @keydown="keydown"
                    v-on="inputAdditionalHandlers"
                )

        transition(
            mode="out-in"
            name="fade-1"
        )
            div(
                v-if="hasIcon && !disabled"
                class="input-icon-wrapper"
            )
                tl-button(
                    v-if="typePassword"
                    color="gray"
                    icon
                    outlined
                    small
                    @click="showPassword = !showPassword"
                )
                    transition(
                        mode="out-in"
                        name="fade-slide-y-1"
                    )
                        i(
                            v-if="type == 'password'"
                            class="mi-eye"
                        )
                        i(
                            v-else
                            class="mi-eye-off"
                        )
                div(
                    v-else
                    class="input-icon"
                )
                    slot(
                        name="right-icon"
                    )
    tl-input-error-text(
        v-if="!hideError"
        :error="error"
    )
</template>

<script setup>
import { computed, onMounted, ref, watch, inject, useSlots, nextTick } from 'vue'
import uniqid from 'uniqid'
import useRules from './validator/rules'
import { useEventBus } from '@vueuse/core'
import { getInjects } from '@/components/$tl/utils/injects'
import { isNotNil } from 'ramda'

const props = defineProps({
    type: String,
    step: [String, Number],
    modelValue: [String, Number],
    label: String,
    opaq: Boolean,
    plain: Boolean,
    focus: Boolean,
    disabled: Boolean,
    rules: Array,
    dense: Boolean,
    external: Boolean,
    error: [String, Boolean],
    hideError: Boolean,
    maxLength: [Number, String],
    pattern: RegExp,
    inputAttrs: [Object],
})

const emit = defineEmits(['validated', 'resetError', 'submit', 'focused', 'update:modelValue', 'mounted'])

const injects = getInjects(props, inject)
const { validateLogic } = useRules()

const id = ref(null)
const input = ref(null)
const modelState = ref(null)
const showPassword = ref(null)
const internalError = ref(null)
const isFocused = ref(false)

const modelValue = computed(() => props.modelValue ?? injects.modelValue?.value)
const label = computed(() => props.label ?? injects.label?.value)
const opaq = computed(() => props.opaq ?? injects.opaq?.value)
const plain = computed(() => props.plain ?? injects.plain?.value)
// const focus = computed(() => props.focus ?? injects.focus?.value)
const disabled = computed(() => props.disabled ?? injects.disabled?.value)
// const rules = computed(() => props.rules ?? injects.rules?.value)
const dense = computed(() => props.dense ?? injects.dense?.value)
// const external = computed(() => props.external ?? injects.external?.value)
const error = computed(() => (props.error ? props.error : internalError.value))
const hideError = computed(() => props.hideError ?? injects.hideError?.value)
const maxLength = computed(() => props.maxLength ?? injects.maxLength?.value)
const typePassword = computed(() => props.type === 'password')
const slots = useSlots()
const hasIcon = computed(() => typePassword.value || !!slots['right-icon'])
const inputAttrs = computed(() => props.inputAttrs ?? injects.inputAttrs?.value ?? {})

const type = computed(() => {
    if (props.type === 'number') {
        return props.type
    }

    if (!typePassword.value || showPassword.value) {
        return 'text'
    }
    return 'password'
})

const step = computed(() => type.value === 'number'
    ? props.step
    : undefined,
)

const inputAdditionalHandlers = computed(() => {
    const handlers = {}

    if (props.pattern) {
        handlers.keypress = keypress
    }

    return handlers
})

modelState.value = modelValue.value

const resetError = () => {
    internalError.value = null
    emit('resetError')
}

const focusInput = () => {
    if (props.disabled) return
    input.value.focus()
    isFocused.value = true
}

const keypress = (event) => {
    if (props.pattern) {
        const char = String.fromCharCode(event.keyCode)
        if (!(props.pattern.test(char))) {
            event.preventDefault()
            return
        }
    }
}

const keydown = (event) => {
    if (props.external) {
        modelState.value = ''
    }
    if (error.value && modelState.value.length) {
        resetError()
    }
    if (event.keyCode === 13) {
        emit('submit')
    }
}

const updateModelState = (event) => {
    let value = event.target.value

    if (
        type.value === 'number' &&
        value !== '' &&
        new RegExp(/^-?\d*\.{0,1}\d+$/g).test(Number(value))
    ) {
        const { max, min } = inputAttrs.value

        if (isNotNil(max) && parseInt(value) > parseInt(max)) {
            value = max
        }

        if (isNotNil(min) && parseInt(value) < parseInt(min)) {
            value = min
        }


        if (value !== event.target.value) {
            // set correct value in input
            input.value.value = value
        }
    }

    modelState.value = value
}

watch(modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val

    if (error.value && modelState.value.length) {
        resetError()
    }
})

watch(modelState, (val) => emit('update:modelValue', val))
watch(isFocused, (val) => emit('focused', val))

onMounted(() => {
    if (props.focus) nextTick(focusInput)

    id.value = uniqid()
    useEventBus(`validate-${id.value}`).on(() => {
        validateLogic(props, modelState, input, internalError, emit)
    })

    useEventBus(`clear-error-${id.value}`).on(() => {
        resetError()
    })

    emit('mounted', input.value)
})

defineExpose({
    isFocused,
})
</script>

<style lang="sass" scoped>
.input-wrapper
    position: relative
    .input-icon-wrapper
        width: 30px
        height: 30px
        position: absolute
        right: 10px
        top: 50%
        margin-top: -15px
    .input-icon
        display: flex
        align-items: center
        justify-content: center
        width: 100%
        height: 100%
</style>
